// Integer-only RNG + value noise. Bit-identical across JS engines (no Math.sin / Math.pow),
// so seed-sharing and golden tests hold cross-platform. (TECHNICAL_DESIGN.md §4.)

/** splitmix32 — one step; returns a uint32. */
export function splitmix32(a: number): number {
  a |= 0;
  a = (a + 0x9e3779b9) | 0;
  let t = a ^ (a >>> 16);
  t = Math.imul(t, 0x21f0aaad);
  t = t ^ (t >>> 15);
  t = Math.imul(t, 0x735a2d97);
  return (t ^ (t >>> 15)) >>> 0;
}

/** Hash two signed ints + a seed into a uint32. */
export function hash2i(seed: number, x: number, y: number): number {
  let h = seed | 0;
  h = Math.imul(h ^ (x | 0), 0x27d4eb2f);
  h = Math.imul(h ^ (y | 0), 0x165667b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return h >>> 0;
}

/** Hash three signed ints + a seed into a uint32. */
export function hash3i(seed: number, a: number, b: number, c: number): number {
  let h = seed | 0;
  h = Math.imul(h ^ (a | 0), 0x27d4eb2f);
  h = Math.imul(h ^ (b | 0), 0x165667b1);
  h = Math.imul(h ^ (c | 0), 0x85ebca77);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 13;
  return h >>> 0;
}

/** uint32 → float in [0,1). */
export const toUnit = (u: number): number => (u >>> 0) / 4294967296;

/** A small stateful stream (for loot etc.); seed with any int. */
export class Rng {
  private s: number;
  constructor(seed: number) {
    this.s = seed | 0;
  }
  next(): number {
    this.s = (this.s + 0x9e3779b9) | 0;
    return splitmix32(this.s);
  }
  float(): number {
    return toUnit(this.next());
  }
  int(nExclusive: number): number {
    return this.next() % nExclusive;
  }
  pick<T>(arr: readonly T[]): T {
    return arr[this.int(arr.length)];
  }
}

/** Smooth interpolation weight (quintic; no pow). */
function smooth(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Deterministic value noise in [0,1] at world-tile coords, given a per-channel salt. */
export function valueNoise(seed: number, salt: number, x: number, y: number, scale: number): number {
  const fx = x / scale;
  const fy = y / scale;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = smooth(fx - x0);
  const ty = smooth(fy - y0);
  const s = (seed ^ Math.imul(salt, 0x9e3779b9)) | 0;
  const c00 = toUnit(hash2i(s, x0, y0));
  const c10 = toUnit(hash2i(s, x0 + 1, y0));
  const c01 = toUnit(hash2i(s, x0, y0 + 1));
  const c11 = toUnit(hash2i(s, x0 + 1, y0 + 1));
  const top = c00 + (c10 - c00) * tx;
  const bot = c01 + (c11 - c01) * tx;
  return top + (bot - top) * ty;
}

/** Fractal (2-octave) value noise for smoother large features. */
export function fbm2(seed: number, salt: number, x: number, y: number, scale: number): number {
  const a = valueNoise(seed, salt, x, y, scale);
  const b = valueNoise(seed, salt + 1, x, y, scale / 2);
  return (a * 2 + b) / 3;
}
